import React, { useState, useRef } from "react";
import axios from "axios";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Box, IconButton, Button, FormControl, CircularProgress } from "@mui/material";
import { bindActionCreators } from "redux";
import { useDispatch, useSelector } from "react-redux";
import { Loader } from "@egovernments/digit-ui-react-components";
import { getDocumentByFileStoreId } from "@egovernments/digit-ui-libraries/src/utils/fileType";
import { registration } from "@egovernments/digit-ui-module-core/src/redux/actions";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import { Controller, useForm } from "react-hook-form";

export default function FileUploader({
  name,
  label,
  hasLabel = true,
  isMandatory = false,
  validationMsg,
  required,
  tab,
  showError,
  setShowError,
  setFile,
  maxFiles = 1,
  disabled,
  existingDocs
}) {
  const { applicationDocuments } = useSelector((state) => state.registration);
  const dispatch = useDispatch();
  const { addRegistrationDoc, removeRegistrationDoc } = bindActionCreators(registration, dispatch);
  const { control } = useForm();
  const fileInputRef = useRef();
  const tenantId = Digit.ULBService.getCurrentTenantId();

  const [isLoading, setIsLoading] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [showToast, setShowToast] = useState(null);

  const uploadedFiles = applicationDocuments?.filter((doc) => doc.documentType === name) || [];
  const isAccepted = uploadedFiles?.[0]?.documentStatus === "Accepted"

  const handleFileUpload = async (e, inputName) => {
    const file = e.target.files[0];
    setIsLoading(true);
    setShowToast(null);
    if (!file) {
      setShowToast({ error: true, label: "Please select a file." });
      setIsLoading(false);
      return;
    }

    if (applicationDocuments?.filter((doc) => doc.documentType === name).length >= maxFiles) {
      setShowToast({ error: true, label: `You can upload a maximum of ${maxFiles} file(s).` });
      setIsLoading(false);
      return;
    }
    const invalidMalwareExtensions = [
      "php",
      "php3",
      "php4",
      "php5",
      "phtml",
      "asp",
      "aspx",
      "jsp",
      "jspx",
      "cfm",
      "exe",
      "bat",
      "cmd",
      "sh",
      "com",
      "scr",
      "msi",
      "jar",
      "vbs",
      "ps1",
      "dll",
      "docm",
      "xlsm",
      "pptm",
      "dotm",
      "xltm",
      "html",
      "htm",
      "js",
      "xml",
      "svg",
      "zip",
      "rar",
      "7z",
      "tar",
      "gz",
      "iso",
    ];
    const fileParts = file.name.toLowerCase().split(".");
    const finalExt = fileParts[fileParts.length - 1];
    const intermediateParts = fileParts.slice(0, -1);
    const hasMaliciousExtension = intermediateParts.some((part) => invalidMalwareExtensions.includes(part));
    const fileExtension = file.name.split(".").pop();
    const ext = `.${fileExtension}`;
    let allowedFormats = [".pdf", ".PDF"];
    let maxSizeInBytes = 30 * 1024 * 1024;
    if (tab === 1) {
      allowedFormats = [".png", ".jpg", ".jpeg", ".JPG", ".PNG", ".JPEG"];
      maxSizeInBytes = 10 * 1024 * 1024;
    } else if (tab === 3 || tab === 5) {
      allowedFormats = [".dxf", ".DXF"];
      maxSizeInBytes = 60 * 1024 * 1024;
    }
    if (hasMaliciousExtension) {
      setShowToast({
        error: true,
        label: "Invalid file name: contains potentially dangerous extensions.",
      });
      setIsLoading(false);
      return;
    }
    if (!allowedFormats.includes(ext)) {
      setShowToast({ error: true, label: `Invalid format. Allowed: ${allowedFormats.join(", ")}` });
      setIsLoading(false);
      return;
    }

    if (file.size > maxSizeInBytes) {
      setShowToast({ error: true, label: `File size exceeds ${maxSizeInBytes / (1024 * 1024)} MB limit.` });
      setIsLoading(false);
      return;
    }

    if (tab === 3 || tab === 5) {
      setFile?.(file);
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("tenantId", tenantId);
    formData.append("module", "property-upload");
    formData.append("tag", "tag-property");

    try {
      const resp = await axios.post("/filestore/v1/files", formData, {});
      const filesId = resp.data.files[0];
      const existingDoc = existingDocs?.find((doc) => doc.documentType === inputName);
      console.log(existingDoc, applicationDocuments, inputName, "exsistingDoc");

      addRegistrationDoc({
        id: existingDoc?.id,
        documentType: inputName,
        fileStoreId: filesId.fileStoreId,
        isMandatory,
        additionalDetails: { fileName: file.name, title: label },
      });
      e.target.value = null;
    } catch (err) {
      console.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const uploadedDocs = applicationDocuments?.filter((doc) => doc.documentType === name) || [];

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <div className="inputContainer" style={{ alignItems: "flex-start" }}>
        {hasLabel && (
          <h3 className="label">
            {label}
            {isMandatory && <span className="compulsoryField"> *</span>}
          </h3>
        )}

        <Box sx={{ flexDirection: "column", position: "relative", width: "100%", display: "flex" }}>
          <FormControl fullWidth sx={{ display: "flex", alignItems: "center" }}>
            <Button
              variant="outlined"
              component="span"
              onClick={() => fileInputRef.current.click()}
              sx={{
                width: "100%",
                mb: 1,
                display: "flex",
                justifyContent: "space-between",
                height: "30px",
                backgroundColor: "#157cb3",
                color: "white",
                "&:hover": {
                  backgroundColor: "#004e6c",
                },
              }}
              disabled={uploadedDocs.length >= maxFiles}
            >
              {isLoading ? (
                <Box sx={{ justifyContent: "center", position: "absolute", alignItems: "center", display: "flex", inset: 0 }}>
                  <CircularProgress sx={{ color: "#ffffff" }} size={28} />
                </Box>
              ) : (
                <React.Fragment>
                  <CloudUploadOutlinedIcon />
                  <span style={{ position: "absolute", fontSize: "0.5rem", marginLeft: "8em" }}>{validationMsg}</span>
                </React.Fragment>
              )}
            </Button>

            <Controller
              name="fileInput"
              control={control}
              rules={{
                required: required && uploadedDocs.length === 0,
              }}
              render={({ field }) => (
                <input
                  {...field}
                  ref={fileInputRef}
                  type="file"
                  name={name}
                  style={{ display: "none" }}
                  onChange={(e) => handleFileUpload(e, name)}
                  disabled={disabled || isAccepted}
                />
              )}
            />
          </FormControl>
        </Box>
      </div>

      {/* Uploaded File Preview */}
      {uploadedDocs.map((doc) => (
        <Box
          sx={{
            justifyContent: "space-between",
            p: "0.25rem 0 0.25rem 0.75rem",
            backgroundColor: "#eee",
            borderRadius: "0.25rem",
            wordBreak: "break-all",
            alignItems: "center",
            display: "flex",
            lineHeight: 1,
            flex: 1,
            mb: 0.5,
            mt: 1,
            height: "18px",
            fontSize: "0.6rem",
          }}
          key={doc.fileStoreId}
          className="file-wrapper-name"
        >
          {doc.additionalDetails?.fileName || doc.documentType}
          <Box sx={{ display: "flex" }}>
            <IconButton
              size="small"
              color="info"
              sx={{ fontSize: "1rem" }}
              onClick={() => getDocumentByFileStoreId(doc.fileStoreId, setIsPreviewLoading)}
            >
              {isPreviewLoading ? <CircularProgress size={20} /> : <VisibilityIcon />}
            </IconButton>
            <IconButton size="small" color="error" sx={{ fontSize: "1rem" }} onClick={() => removeRegistrationDoc(doc.documentType)} disabled={doc?.documentStatus === "Accepted"}>
              <DeleteForeverIcon />
            </IconButton>
          </Box>
        </Box>
      ))}

      {/* Error Message */}
      {required && showError && uploadedDocs.length === 0 && (
        <span className="errorText">{`${hasLabel ? label : "Above"} document is required.`}</span>
      )}
      {!!showToast?.error && <span className="errorText">{showToast.label}</span>}
    </Box>
  );
}
